import React, { useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Card, Icon, Table } from '@syneto/compass-react';
import { ServiceContext } from '../../contexts/ServiceContext';
import { NotificationContext } from '../../contexts/NotificationContext';
import { Loading } from '../../components/Loading';
import { LoadFailedMessage } from '../../components/LoadFailedMessage';

// The Community keys page (SYN-3463): a Hub user generates an activation key for the company,
// copies it and takes it to the machine's activation form, and sees the company's keys newest
// first, a page at a time. The list is paged by rest-api (page, pageSize, sort=-createdAt), so
// the table only ever holds the page on screen and asks for another page on demand.
export const COMMUNITY_KEYS_PAGE_SIZE = 20;
export const KEY_COPIED_MESSAGE = 'The key has been copied to the clipboard.';
export const KEY_COPY_FAILED_MESSAGE = 'The key could not be copied. Select it and copy it by hand.';
export const KEY_GENERATED_MESSAGE = 'Your Community key is ready.';
export const NO_KEYS_MESSAGE = 'Your company has no Community keys yet. Generate one above.';
export const NOT_ACTIVATED_LABEL = 'Not activated';
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const formatDateTime = (datetime) => {
	return moment(datetime).format(DATE_TIME_FORMAT);
};

export const CommunityKeys = () => {
	const { licensingService } = useContext(ServiceContext);
	const { pushDangerNotification, pushSuccessfulNotification } = useContext(NotificationContext);

	const [joinPreviewProgram, setJoinPreviewProgram] = useState(true);
	const [generating, setGenerating] = useState(false);
	const [generatedKey, setGeneratedKey] = useState(null);

	const [page, setPage] = useState(1);
	const [keys, setKeys] = useState([]);
	const [totalItems, setTotalItems] = useState(0);
	const [listLoading, setListLoading] = useState(true);
	const [listLoadFailed, setListLoadFailed] = useState(false);
	// Bumped to read the current page again: after a generated key, or from the retry button.
	const [listReloadCount, setListReloadCount] = useState(0);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			setListLoading(true);
			try {
				const { items, pagination } = await licensingService.getCommunityKeys(page, COMMUNITY_KEYS_PAGE_SIZE);
				if (!cancelled) {
					setKeys(items);
					setTotalItems(pagination.totalItems ?? items.length);
					setListLoadFailed(false);
				}
			} catch (error) {
				if (!cancelled) {
					setListLoadFailed(true);
					pushDangerNotification(error);
				}
			} finally {
				if (!cancelled) {
					setListLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [page, listReloadCount]);

	const reloadList = useCallback(() => {
		setListReloadCount((count) => { return count + 1; });
	}, []);

	// The clipboard is only there on a secure page (https, localhost); elsewhere the user is told
	// to copy the key by hand rather than left with a click that did nothing.
	const copyKey = async (key) => {
		try {
			await navigator.clipboard.writeText(key);
			pushSuccessfulNotification(KEY_COPIED_MESSAGE);
		} catch (error) {
			pushDangerNotification(KEY_COPY_FAILED_MESSAGE);
		}
	};

	// A failed Generate creates nothing, so the previous new key (already in the list) is taken
	// off the "new key" spot: nothing on the page may pass for the key that was not made.
	const generateKey = async () => {
		setGenerating(true);
		try {
			const key = await licensingService.createCommunityKey(joinPreviewProgram);
			setGeneratedKey(key);
			pushSuccessfulNotification(KEY_GENERATED_MESSAGE);
			setPage(1);
			reloadList();
		} catch (error) {
			setGeneratedKey(null);
			pushDangerNotification(error);
		} finally {
			setGenerating(false);
		}
	};

	const onTableChange = (type, { page: requestedPage }) => {
		if (type === 'pagination' && requestedPage !== page) {
			setPage(requestedPage);
		}
	};

	const renderCopyButton = (key) => {
		return (
			<button type="button" className="btn btn-link p-0 ms-2 align-baseline" aria-label={`Copy key ${key}`} onClick={() => { return copyKey(key); }}>
				<Icon name="fa fa-copy" />
			</button>
		);
	};

	const renderKey = (cell, row) => {
		return (
			<span className="text-nowrap">
				<code className="text-body">{row.key}</code>
				{renderCopyButton(row.key)}
			</span>
		);
	};

	const renderCreatedAt = (cell, row) => {
		return formatDateTime(row.createdAt);
	};

	const renderSerialNumber = (cell, row) => {
		return row.serialNumber ?? '';
	};

	const renderActivatedAt = (cell, row) => {
		return row.activatedAt ? formatDateTime(row.activatedAt) : <span className="text-muted">{NOT_ACTIVATED_LABEL}</span>;
	};

	const renderList = () => {
		if (listLoading) {
			return <Loading />;
		}

		if (listLoadFailed) {
			return <LoadFailedMessage subject="The Community keys" onRetry={reloadList} />;
		}

		if (totalItems === 0) {
			return <p className="text-muted text-center my-5">{NO_KEYS_MESSAGE}</p>;
		}

		return (
			<Table
				data={keys}
				keyField="id"
				remote={{ pagination: true }}
				onTableChange={onTableChange}
				pagination={{
					page,
					sizePerPage: COMMUNITY_KEYS_PAGE_SIZE,
					totalSize: totalItems,
					hideSizePerPage: true,
					sizePerPageList: [COMMUNITY_KEYS_PAGE_SIZE],
				}}
			>
				<Table.Col field="key" align="left" renderCell={renderKey}>
					Key
				</Table.Col>
				<Table.Col field="createdAt" align="left" renderCell={renderCreatedAt}>
					Created
				</Table.Col>
				<Table.Col field="serialNumber" align="left" renderCell={renderSerialNumber}>
					Serial number
				</Table.Col>
				<Table.Col field="activatedAt" align="left" renderCell={renderActivatedAt}>
					Activated
				</Table.Col>
			</Table>
		);
	};

	return (
		<div className="mt-5 ms-5 me-5 mb-5">
			<h1 className="text-muted font-weight-light">Community Keys</h1>
			<p className="text-muted font-weight-light">
				A Community key activates a SynetoOS 6.1 machine with the Community edition. Generate one, copy it and
				paste it in the machine&apos;s activation form.
			</p>

			<Card
				className="shadow mt-4"
				content={
					<>
						<h5 className="text-muted font-weight-light border-bottom pb-3 mb-0">Generate a key</h5>
						<div className="d-flex align-items-center flex-wrap gap-4 pt-3">
							<div>
								<Button width={128} onClick={generateKey} loading={generating ? 'Generating...' : false} disabled={generating}>
									Generate
								</Button>
							</div>
							<div className="form-check">
								<input
									className="form-check-input"
									type="checkbox"
									id="join-preview-program"
									checked={joinPreviewProgram}
									disabled={generating}
									onChange={(event) => { return setJoinPreviewProgram(event.target.checked); }}
								/>
								<label className="form-check-label" htmlFor="join-preview-program">
									Join the Preview Program
								</label>
							</div>
						</div>
						{generatedKey && (
							<div role="status" className="mt-3 p-3 bg-light rounded">
								<span className="text-muted">Your new key: </span>
								<code className="text-body fs-5">{generatedKey.key}</code>
								{renderCopyButton(generatedKey.key)}
							</div>
						)}
					</>
				}
			/>

			<Card
				className="shadow mt-4"
				content={
					<>
						<h5 className="text-muted font-weight-light border-bottom pb-3 mb-0">Your company&apos;s keys</h5>
						<div className="pt-3" data-testid="community-keys-list">{renderList()}</div>
					</>
				}
			/>
		</div>
	);
};
